import { useContext } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, Spinner } from '@design-system';
import { OnboardingFooterBar } from '@/features/client-onboarding';
import { ConsumerFooterSlot } from './consumer-footer-slot';

export interface StepAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

interface StepFooterProps {
  onBack?: () => void;
  backLabel?: string;
  primary?: StepAction;
  secondary?: StepAction;
}

/** The Back / Next bar, placed in the layout's page-wide footer when there is one. */
export function StepFooter({ onBack, backLabel = 'Back', primary, secondary }: StepFooterProps) {
  const footerSlot = useContext(ConsumerFooterSlot);
  if (!onBack && !primary && !secondary) return null;

  const bar = (
    <OnboardingFooterBar
      back={
        onBack && (
          <Button variant="outline" size="lg" className="rounded-full" onClick={onBack}>
            <ChevronLeft />
            {backLabel}
          </Button>
        )
      }
      actions={
        <>
          {secondary && (
            <Button
              variant="outline"
              size="lg"
              className="rounded-full"
              onClick={secondary.onClick}
              disabled={secondary.disabled}
            >
              {secondary.label}
            </Button>
          )}
          {primary && (
            <Button
              size="lg"
              className="rounded-full"
              onClick={primary.onClick}
              disabled={primary.disabled || primary.loading}
              data-testid="consumer-primary-action"
            >
              {primary.loading && <Spinner />}
              {primary.label}
              <ChevronRight />
            </Button>
          )}
        </>
      }
    />
  );

  return footerSlot ? createPortal(bar, footerSlot) : bar;
}
